// The action journal, `/agents/journal` (spec 14 §4.4, D-512 ruling 7): the
// one approve and undo surface. Every office role reads it; the server serves
// each reader the cards it passes §7.2's checks 1 and 3 for, and computes
// `can_approve` and `can_undo` from exactly those checks, so this screen never
// decides who may act. Money is omitted for a reader without the tool's value
// grants (§7.5), and the impact filter is offered only to a reader the page
// has served money to.
//
// States: "Nothing waiting on you" on an empty "Needs my decision", five card
// skeletons while loading, a retry banner on error. At phone width the
// DataTable collapses to single-column cards with full-width Approve and
// Reject and a prominent countdown.

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { PageHeader } from "../../../../components/breadcrumbs";
import { Button } from "../../../../components/button";
import { Card } from "../../../../components/card";
import { DataTable, type DataTableColumn } from "../../../../components/data-table";
import { Field } from "../../../../components/field";
import { Modal } from "../../../../components/modal";
import { Select } from "../../../../components/select";
import { CardSkeleton, EmptyState } from "../../../../components/states";
import { Tabs } from "../../../../components/tabs";
import { Banner, toast } from "../../../../components/toast";
import { formatTimestamp } from "../../../../lib/datetime";
import { UnauthorizedError } from "../../../../lib/problem";
import { minorUnitDigits, normalizeTypedDecimal } from "../../sales/lib/money";
import { ActionBody, ActionStatusChip, Countdown, ImpactChip } from "../components/action-card";
import {
  agentsKeys,
  approveAction,
  problemCode,
  rejectAction,
  undoAction,
  useAction,
  useActionPage,
  useAgentDepartments,
  useAgentFacilities,
  useAgentTools,
  useAgents,
  type ActionListQuery,
} from "../hooks/agents-queries";
import { useAgentsPermissions } from "../hooks/permissions";
import {
  BULK_APPROVE_MAX,
  JOURNAL_TABS,
  bulkSelectable,
  dayEndIso,
  dayStartIso,
  majorToMinor,
  minorToMajorText,
  resultHref,
  tabQuery,
  undoAffordance,
  type AgentAction,
  type JournalSearch,
  type JournalSortKey,
  type JournalTab,
} from "../lib/agents-lib";
import { agentName, compensationText, errorText, toolName } from "../lib/labels";

const PAGE = 25;

type Dialog =
  | { readonly kind: "reject"; readonly action: AgentAction }
  | { readonly kind: "undo"; readonly action: AgentAction }
  | { readonly kind: "manual"; readonly action: AgentAction };

function useNow(intervalMs: number): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => {
      setNow(Date.now());
    }, intervalMs);
    return () => {
      clearInterval(id);
    };
  }, [intervalMs]);
  return now;
}

function ImpactFilter({
  value,
  currency,
  onChange,
}: {
  value: number | undefined;
  currency: string | undefined;
  onChange: (minor: number | undefined) => void;
}) {
  const { t, i18n } = useTranslation();
  const digits = currency === undefined ? 2 : minorUnitDigits(currency, i18n.language);
  const [text, setText] = useState(value === undefined ? "" : minorToMajorText(value, digits));
  const [invalid, setInvalid] = useState(false);
  const commit = () => {
    if (text.trim() === "") {
      setInvalid(false);
      onChange(undefined);
      return;
    }
    const minor = majorToMinor(normalizeTypedDecimal(text, i18n.language), digits);
    setInvalid(minor === undefined);
    if (minor !== undefined) onChange(minor);
  };
  return (
    <Field
      label={
        currency === undefined
          ? t("agents.journal.filters.impactNoCurrency")
          : t("agents.journal.filters.impact", { currency })
      }
    >
      <input
        className="ds-input ag-input-narrow"
        inputMode="decimal"
        value={text}
        aria-invalid={invalid}
        onChange={(e) => {
          setText(e.target.value);
        }}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit();
        }}
        data-testid="journal-filter-impact"
      />
    </Field>
  );
}

export function AgentsJournalScreen({
  search,
  onSearch,
}: {
  search: JournalSearch;
  onSearch: (next: JournalSearch) => void;
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const perms = useAgentsPermissions();
  const now = useNow(30_000);
  const tab: JournalTab = search.tab ?? "mine";
  const [cursors, setCursors] = useState<readonly (string | undefined)[]>([undefined]);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [dialog, setDialog] = useState<Dialog | undefined>(undefined);
  const [note, setNote] = useState("");
  const [pending, setPending] = useState<ReadonlySet<string>>(new Set());

  const query: ActionListQuery = {
    ...tabQuery(tab),
    agent_id: search.agent,
    tool_code: search.tool,
    facility_id: search.facility,
    department_id: search.department,
    impact_min_minor: search.impact,
    from: search.from !== undefined ? dayStartIso(search.from) : undefined,
    to: search.to !== undefined ? dayEndIso(search.to) : undefined,
    sort: search.sort,
  };
  const cursor = cursors[cursors.length - 1];
  const page = useActionPage(query, cursor);
  // The server walks a bounded number of rows per request, so a page can
  // come back empty with a cursor while later rows remain (D-543 ruling 8):
  // step past it in place, so Back returns to the last page with cards.
  const emptyNext = page.data?.items.length === 0 ? page.data.next_cursor : null;
  useEffect(() => {
    if (emptyNext === null) return;
    setCursors((prev) => [...prev.slice(0, -1), emptyNext]);
  }, [emptyNext]);
  const focus = useAction(search.action);
  const agents = useAgents(perms.canList);
  const tools = useAgentTools(perms.canRead);
  const facilities = useAgentFacilities();
  const departments = useAgentDepartments(search.facility);

  useEffect(() => {
    if (page.error instanceof UnauthorizedError) void navigate({ to: "/login" });
  }, [page.error, navigate]);

  const rows = useMemo(() => page.data?.items ?? [], [page.data]);
  const byAgent = new Map((agents.data ?? []).map((a) => [a.id, a]));
  const selectedRows = rows.filter((r) => selected.has(r.id));
  const moneyServed = rows.find(
    (r) => r.impact_minor !== undefined && r.impact_currency !== undefined,
  );
  const impactFilterShown = moneyServed !== undefined || search.impact !== undefined;
  const impactRefused = problemCode(page.error) === "AGENT_IMPACT_FILTER_REQUIRES_VALUE";

  const resetPaging = () => {
    setCursors([undefined]);
    setSelected(new Set());
  };
  const update = (next: Partial<JournalSearch>) => {
    resetPaging();
    onSearch({ ...search, ...next, action: undefined });
  };
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: agentsKeys.actions() });
    void queryClient.invalidateQueries({ queryKey: agentsKeys.list() });
  };

  const run = async (action: AgentAction, work: () => Promise<string>) => {
    setPending((prev) => new Set(prev).add(action.id));
    try {
      toast.success(await work());
    } catch (error) {
      toast.error(errorText(t, error));
    } finally {
      setPending((prev) => {
        const next = new Set(prev);
        next.delete(action.id);
        return next;
      });
      refresh();
    }
  };

  const approve = (action: AgentAction) =>
    run(action, async () => {
      const result = await approveAction(action.id);
      return result.status === "executing"
        ? t("agents.journal.approvedExecuting")
        : t("agents.journal.approved");
    });

  const bulkApprove = async () => {
    const chosen = selectedRows.slice(0, BULK_APPROVE_MAX);
    let ok = 0;
    let failed = 0;
    for (const action of chosen) {
      try {
        await approveAction(action.id);
        ok += 1;
      } catch {
        failed += 1;
      }
    }
    setSelected(new Set());
    refresh();
    if (failed === 0) toast.success(t("agents.journal.bulk.done", { count: ok }));
    else toast.error(t("agents.journal.bulk.partial", { ok, failed }));
  };

  const decideCell = (action: AgentAction) => {
    const busy = pending.has(action.id);
    const undo = undoAffordance(action);
    const href = action.status === "executed" ? resultHref(action.result) : undefined;
    return (
      <div className="ag-decide" data-testid="action-decide">
        {action.status === "proposed" && action.can_approve ? (
          <>
            <Button
              variant="primary"
              disabled={busy}
              onClick={() => {
                void approve(action);
              }}
              data-testid="action-approve"
            >
              {t("agents.journal.approve")}
            </Button>
            <Button
              disabled={busy}
              onClick={() => {
                setNote("");
                setDialog({ kind: "reject", action });
              }}
              data-testid="action-reject"
            >
              {t("agents.journal.reject")}
            </Button>
          </>
        ) : null}
        {href !== undefined ? (
          <a
            href={href}
            className="ag-link"
            onClick={(e) => {
              e.preventDefault();
              void navigate({ href });
            }}
            data-testid="action-result"
          >
            {t("agents.journal.result")}
          </a>
        ) : null}
        {undo === "post" ? (
          <Button
            disabled={busy}
            onClick={() => {
              setNote("");
              setDialog({ kind: "undo", action });
            }}
            data-testid="action-undo"
          >
            {t("agents.journal.undo")}
          </Button>
        ) : null}
        {undo === "manual" ? (
          <Button
            onClick={() => {
              setDialog({ kind: "manual", action });
            }}
            data-testid="action-undo-manual"
          >
            {t("agents.journal.undo")}
          </Button>
        ) : null}
      </div>
    );
  };

  const columns: DataTableColumn<AgentAction, JournalSortKey>[] = [
    {
      key: "action",
      header: t("agents.journal.columns.action"),
      kind: "id",
      phonePriority: 1,
      render: (row) => toolName(t, row.tool_code),
    },
    {
      key: "agent",
      header: t("agents.journal.columns.agent"),
      phonePriority: 4,
      render: (row) => {
        const agent = byAgent.get(row.agent_id);
        return agent !== undefined
          ? agentName(t, agent)
          : agentName(t, { display_name: null, agent_code: row.agent_code });
      },
    },
    {
      key: "impact",
      header: t("agents.journal.columns.impact"),
      phonePriority: 2,
      presence: (list) =>
        list.some((r) => r.impact_minor !== undefined || (r.impact_count ?? 0) > 0),
      render: (row) => <ImpactChip action={row} />,
    },
    {
      key: "created",
      header: t("agents.journal.columns.created"),
      kind: "date",
      sortKey: "created_at",
      sortFirst: "desc",
      render: (row) => formatTimestamp(row.created_at, i18n.language),
    },
    {
      key: "expires",
      header: t("agents.journal.columns.expires"),
      sortKey: "expires_at",
      phonePriority: 3,
      render: (row) =>
        row.status === "proposed" ? (
          <Countdown action={row} now={now} />
        ) : (
          formatTimestamp(row.expires_at, i18n.language)
        ),
    },
    {
      key: "status",
      header: t("agents.journal.columns.status"),
      kind: "status",
      render: (row) => <ActionStatusChip action={row} />,
    },
    {
      key: "decide",
      header: t("agents.journal.columns.decide"),
      actions: true,
      render: decideCell,
    },
  ];

  const pageIndex = cursors.length;
  const bulkable = tab === "mine" || tab === "proposed";
  const toolOptions = perms.canRead
    ? (tools.data ?? []).filter((tool) => tool.kind === "write").map((tool) => tool.tool_code)
    : [...new Set([...rows.map((r) => r.tool_code), ...(search.tool ? [search.tool] : [])])];

  const empty = (
    <EmptyState
      title={tab === "mine" ? t("agents.journal.empty.mine") : t("agents.journal.empty.other")}
      body={t("agents.journal.empty.body")}
    />
  );

  return (
    <div className="ag-stack" data-testid="agents-journal">
      <PageHeader
        title={t("agents.journal.title")}
        lede={t("agents.journal.lede")}
        {...(perms.canList ? { crumbs: [{ label: t("agents.nav.hub"), to: "/agents" }] } : {})}
      />
      {search.action !== undefined ? (
        <Card
          className="ag-focus"
          title={t("agents.journal.focus.title")}
          action={
            <Button
              onClick={() => {
                onSearch({ ...search, action: undefined });
              }}
              data-testid="journal-focus-close"
            >
              {t("agents.journal.focus.close")}
            </Button>
          }
          data-testid="journal-focus"
        >
          <Card.Body>
            {focus.isPending ? (
              <CardSkeleton lines={3} label={t("agents.journal.loading")} />
            ) : focus.data === undefined ? (
              <p className="ag-hint">{t("agents.journal.focus.missing")}</p>
            ) : (
              <div className="ag-stack">
                <div className="ag-row">
                  <strong>{toolName(t, focus.data.tool_code)}</strong>
                  <ActionStatusChip action={focus.data} />
                  <Countdown action={focus.data} now={now} />
                </div>
                <ActionBody action={focus.data} />
                {decideCell(focus.data)}
              </div>
            )}
          </Card.Body>
        </Card>
      ) : null}
      <Tabs
        label={t("agents.journal.tabs.aria")}
        active={tab}
        onSelect={(id) => {
          update({ tab: id === "mine" ? undefined : (id as JournalTab) });
        }}
        tabs={JOURNAL_TABS.map((id) => ({
          id,
          label: t(`agents.journal.tabs.${id}`),
          "data-testid": `journal-tab-${id}`,
        }))}
        data-testid="journal-tabs"
      />
      <div className="ag-filters" role="group" aria-label={t("agents.journal.filters.label")}>
        {perms.canList && (agents.data?.length ?? 0) > 0 ? (
          <Field label={t("agents.journal.filters.agent")}>
            <Select
              value={search.agent ?? ""}
              onChange={(e) => {
                update({ agent: e.target.value === "" ? undefined : e.target.value });
              }}
              data-testid="journal-filter-agent"
            >
              <option value="">{t("agents.common.all")}</option>
              {(agents.data ?? []).map((agent) => (
                <option key={agent.id} value={agent.id}>
                  {agentName(t, agent)}
                </option>
              ))}
            </Select>
          </Field>
        ) : null}
        {toolOptions.length > 0 ? (
          <Field label={t("agents.journal.filters.tool")}>
            <Select
              value={search.tool ?? ""}
              onChange={(e) => {
                update({ tool: e.target.value === "" ? undefined : e.target.value });
              }}
              data-testid="journal-filter-tool"
            >
              <option value="">{t("agents.common.all")}</option>
              {toolOptions.map((code) => (
                <option key={code} value={code}>
                  {toolName(t, code)}
                </option>
              ))}
            </Select>
          </Field>
        ) : null}
        {facilities.data !== undefined && facilities.data.length > 0 ? (
          <Field label={t("agents.journal.filters.facility")}>
            <Select
              value={search.facility ?? ""}
              onChange={(e) => {
                update({
                  facility: e.target.value === "" ? undefined : e.target.value,
                  department: undefined,
                });
              }}
              data-testid="journal-filter-facility"
            >
              <option value="">{t("agents.common.all")}</option>
              {facilities.data.map((facility) => (
                <option key={facility.id} value={facility.id}>
                  {facility.name}
                </option>
              ))}
            </Select>
          </Field>
        ) : null}
        {search.facility !== undefined && (departments.data?.length ?? 0) > 0 ? (
          <Field label={t("agents.journal.filters.department")}>
            <Select
              value={search.department ?? ""}
              onChange={(e) => {
                update({ department: e.target.value === "" ? undefined : e.target.value });
              }}
              data-testid="journal-filter-department"
            >
              <option value="">{t("agents.common.all")}</option>
              {(departments.data ?? []).map((department) => (
                <option key={department.id} value={department.id}>
                  {department.name}
                </option>
              ))}
            </Select>
          </Field>
        ) : null}
        <Field label={t("agents.journal.filters.from")}>
          <input
            type="date"
            className="ds-input"
            value={search.from ?? ""}
            onChange={(e) => {
              update({ from: e.target.value === "" ? undefined : e.target.value });
            }}
            data-testid="journal-filter-from"
          />
        </Field>
        <Field label={t("agents.journal.filters.to")}>
          <input
            type="date"
            className="ds-input"
            value={search.to ?? ""}
            onChange={(e) => {
              update({ to: e.target.value === "" ? undefined : e.target.value });
            }}
            data-testid="journal-filter-to"
          />
        </Field>
        {impactFilterShown ? (
          <ImpactFilter
            value={search.impact}
            currency={moneyServed?.impact_currency}
            onChange={(minor) => {
              update({ impact: minor });
            }}
          />
        ) : null}
        {search.agent !== undefined ||
        search.tool !== undefined ||
        search.facility !== undefined ||
        search.from !== undefined ||
        search.to !== undefined ||
        search.impact !== undefined ? (
          <Button
            onClick={() => {
              update({
                agent: undefined,
                tool: undefined,
                facility: undefined,
                department: undefined,
                from: undefined,
                to: undefined,
                impact: undefined,
              });
            }}
            data-testid="journal-filter-clear"
          >
            {t("agents.journal.filters.clear")}
          </Button>
        ) : null}
      </div>
      {page.isError && !impactRefused ? (
        <Banner kind="bad" data-testid="journal-error">
          <span className="ag-row">
            <span>{t("agents.journal.loadError")}</span>
            <Button
              onClick={() => {
                void page.refetch();
              }}
            >
              {t("agents.common.retry")}
            </Button>
          </span>
        </Banner>
      ) : null}
      {impactRefused ? (
        <Banner kind="warn" data-testid="journal-impact-refused">
          <span className="ag-row">
            <span>{errorText(t, page.error)}</span>
            <Button
              onClick={() => {
                update({ impact: undefined });
              }}
            >
              {t("agents.journal.filters.clear")}
            </Button>
          </span>
        </Banner>
      ) : null}
      {page.isPending ? (
        <div className="ag-stack" data-testid="journal-loading">
          {Array.from({ length: 5 }, (_, i) => (
            <CardSkeleton key={i} lines={2} label={t("agents.journal.loading")} />
          ))}
        </div>
      ) : page.isError ? null : (
        <DataTable<AgentAction, JournalSortKey>
          columns={columns}
          rows={rows}
          rowKey={(row) => row.id}
          rowTestId="journal-row"
          empty={empty}
          expansion={(row) => <ActionBody action={row} />}
          expansionLabel={() => t("agents.journal.evidence")}
          sort={{
            value: search.sort ?? "-created_at",
            onChange: (order) => {
              update({ sort: order });
            },
            toggleLabel: (header, next) =>
              t("agents.common.sortToggle", {
                column: header,
                direction:
                  next === "ascending"
                    ? t("agents.common.ascending")
                    : t("agents.common.descending"),
              }),
          }}
          {...(bulkable
            ? {
                selectable: {
                  selected,
                  onChange: (next: ReadonlySet<string>) => {
                    const chosen = rows.filter((r) => next.has(r.id));
                    const kept: AgentAction[] = [];
                    for (const action of chosen) {
                      if (bulkSelectable(action, kept)) kept.push(action);
                    }
                    setSelected(new Set(kept.map((a) => a.id)));
                  },
                  headerLabel: t("agents.journal.bulk.selectAll"),
                  rowLabel: (row: AgentAction) =>
                    `${t("agents.journal.bulk.select")} ${toolName(t, row.tool_code)}`,
                  isRowSelectable: (row: AgentAction) => bulkSelectable(row, selectedRows),
                  testId: "journal-select",
                },
                bulkBar: {
                  countLabel: `${t("agents.journal.bulk.count", { count: selected.size })} · ${t(
                    "agents.journal.bulk.limit",
                    { max: BULK_APPROVE_MAX },
                  )}`,
                  clearLabel: t("agents.journal.bulk.clear"),
                  onClear: () => {
                    setSelected(new Set());
                  },
                  children: (
                    <Button
                      variant="primary"
                      onClick={() => {
                        void bulkApprove();
                      }}
                      data-testid="journal-bulk-approve"
                    >
                      {t("agents.journal.bulk.approve")}
                    </Button>
                  ),
                },
              }
            : {})}
          pager={
            rows.length > 0 || pageIndex > 1
              ? {
                  range: t("agents.common.pageRange", {
                    from: (pageIndex - 1) * PAGE + 1,
                    to: (pageIndex - 1) * PAGE + rows.length,
                  }),
                  page: pageIndex,
                  onPage: (next) => {
                    if (next > pageIndex && page.data.next_cursor !== null) {
                      setCursors([...cursors, page.data.next_cursor]);
                      setSelected(new Set());
                    } else if (next < pageIndex) {
                      setCursors(cursors.slice(0, -1));
                      setSelected(new Set());
                    }
                  },
                  prevLabel: t("agents.common.previous"),
                  nextLabel: t("agents.common.next"),
                  hasPrev: pageIndex > 1,
                  hasNext: page.data.next_cursor !== null,
                }
              : undefined
          }
          data-testid="journal-table"
        />
      )}
      {dialog?.kind === "reject" ? (
        <Modal
          label={t("agents.journal.rejectTitle")}
          heading={t("agents.journal.rejectTitle")}
          closeLabel={t("agents.common.close")}
          onClose={() => {
            setDialog(undefined);
          }}
          pending={pending.has(dialog.action.id)}
          testId="journal-reject-dialog"
        >
          <div className="ag-stack">
            <p className="ag-hint">{toolName(t, dialog.action.tool_code)}</p>
            <Field label={t("agents.journal.rejectNote")}>
              <textarea
                className="ds-input"
                maxLength={2000}
                value={note}
                onChange={(e) => {
                  setNote(e.target.value);
                }}
                data-testid="journal-reject-note"
              />
            </Field>
            <div className="ag-decide">
              <Button
                onClick={() => {
                  setDialog(undefined);
                }}
              >
                {t("agents.common.cancel")}
              </Button>
              <Button
                variant="danger"
                disabled={pending.has(dialog.action.id)}
                onClick={() => {
                  const action = dialog.action;
                  const trimmed = note.trim();
                  void run(action, async () => {
                    await rejectAction(action.id, trimmed === "" ? undefined : trimmed);
                    return t("agents.journal.rejected");
                  }).then(() => {
                    setDialog(undefined);
                  });
                }}
                data-testid="journal-reject-confirm"
              >
                {t("agents.journal.reject")}
              </Button>
            </div>
          </div>
        </Modal>
      ) : null}
      {dialog?.kind === "undo" ? (
        <Modal
          label={t("agents.journal.undoTitle")}
          heading={t("agents.journal.undoTitle")}
          closeLabel={t("agents.common.close")}
          onClose={() => {
            setDialog(undefined);
          }}
          pending={pending.has(dialog.action.id)}
          testId="journal-undo-dialog"
        >
          <div className="ag-stack">
            <p className="ag-hint">{t("agents.journal.undoBody")}</p>
            <div className="ag-decide">
              <Button
                onClick={() => {
                  setDialog(undefined);
                }}
              >
                {t("agents.common.cancel")}
              </Button>
              <Button
                variant="danger"
                disabled={pending.has(dialog.action.id)}
                onClick={() => {
                  const action = dialog.action;
                  void run(action, async () => {
                    await undoAction(action.id);
                    return t("agents.journal.undone");
                  }).then(() => {
                    setDialog(undefined);
                  });
                }}
                data-testid="journal-undo-confirm"
              >
                {t("agents.journal.undoConfirm")}
              </Button>
            </div>
          </div>
        </Modal>
      ) : null}
      {dialog?.kind === "manual" ? (
        <Modal
          label={t("agents.journal.manualTitle")}
          heading={t("agents.journal.manualTitle")}
          closeLabel={t("agents.common.close")}
          onClose={() => {
            setDialog(undefined);
          }}
          testId="journal-manual-dialog"
        >
          <div className="ag-stack">
            <p data-testid="journal-manual-text">
              {compensationText(t, dialog.action.compensation_key) ?? t("agents.problems.other")}
            </p>
            {resultHref(dialog.action.result) !== undefined ? (
              <a
                href={resultHref(dialog.action.result)}
                className="ag-link"
                onClick={(e) => {
                  e.preventDefault();
                  const href = resultHref(dialog.action.result);
                  if (href !== undefined) void navigate({ href });
                }}
                data-testid="journal-manual-open"
              >
                {t("agents.journal.manualOpen")}
              </a>
            ) : null}
            <div className="ag-decide">
              <Button
                onClick={() => {
                  setDialog(undefined);
                }}
              >
                {t("agents.common.close")}
              </Button>
            </div>
          </div>
        </Modal>
      ) : null}
    </div>
  );
}
